import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { COMPANY } from "../data/content";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "Strategy", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSent(true);
    toast.success("Thank you! Our team will reach out within one business day.");
    setForm({ name: "", email: "", company: "", interest: "Strategy", message: "" });
  };

  const upd = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact Us"
        title={<>Let's <em className="text-brand-green font-serif italic">talk strategy.</em></>}
        subtitle="Partner with Worldly Solutions today and unlock the full potential of your business. Reach out for a confidential consultation."
      />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Reach us</div>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight leading-tight">Speak with a senior consultant.</h2>
            <p className="mt-5 text-brand-slate leading-relaxed">All conversations are confidential. We'll respond within one business day with a proposed agenda.</p>

            <div className="mt-10 space-y-6">
              <div className="flex gap-4">
                <div className="h-11 w-11 rounded-sm bg-brand-green-50 inline-flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-brand-slate">Headquarters</div>
                  <div data-testid="contact-address" className="text-brand-ink mt-1 leading-relaxed">{COMPANY.address}</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-11 w-11 rounded-sm bg-brand-green-50 inline-flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-brand-slate">Email</div>
                  <a data-testid="contact-email" href={`mailto:${COMPANY.email}`} className="text-brand-ink mt-1 block hover:text-brand-green transition-colors">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-11 w-11 rounded-sm bg-brand-green-50 inline-flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-brand-green" />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-brand-slate">Phone</div>
                  <a data-testid="contact-phone" href={`tel:${COMPANY.phone.replace(/[^+\d]/g, '')}`} className="text-brand-ink mt-1 block hover:text-brand-green transition-colors">{COMPANY.phone}</a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="bg-white border border-black/5 shadow-glass rounded-sm p-8 md:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-brand-slate">Full Name *</label>
                  <input data-testid="contact-name" value={form.name} onChange={upd("name")} className="mt-2 w-full bg-transparent border-b-2 border-black/10 focus:border-brand-green outline-none py-3 text-brand-ink transition-colors" placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-brand-slate">Email *</label>
                  <input data-testid="contact-email-input" type="email" value={form.email} onChange={upd("email")} className="mt-2 w-full bg-transparent border-b-2 border-black/10 focus:border-brand-green outline-none py-3 text-brand-ink transition-colors" placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-brand-slate">Company</label>
                  <input data-testid="contact-company" value={form.company} onChange={upd("company")} className="mt-2 w-full bg-transparent border-b-2 border-black/10 focus:border-brand-green outline-none py-3 text-brand-ink transition-colors" placeholder="Acme Corp." />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-brand-slate">Area of Interest</label>
                  <select data-testid="contact-interest" value={form.interest} onChange={upd("interest")} className="mt-2 w-full bg-transparent border-b-2 border-black/10 focus:border-brand-green outline-none py-3 text-brand-ink transition-colors">
                    <option>Strategy</option><option>Operational Excellence</option><option>Digital Transformation</option>
                    <option>Data & Analytics</option><option>Risk & Governance</option><option>Human Capital</option><option>Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="text-xs font-bold tracking-widest uppercase text-brand-slate">How can we help? *</label>
                <textarea data-testid="contact-message" rows="5" value={form.message} onChange={upd("message")} className="mt-2 w-full bg-transparent border-b-2 border-black/10 focus:border-brand-green outline-none py-3 text-brand-ink transition-colors resize-none" placeholder="Tell us about your challenge or opportunity..."></textarea>
              </div>

              <button data-testid="contact-submit" type="submit" className="mt-8 inline-flex items-center gap-3 bg-brand-green text-white px-7 py-4 rounded-sm font-medium hover:bg-brand-green-light transition-colors">
                Send Message <Send className="h-4 w-4" />
              </button>

              {sent && (
                <div data-testid="contact-success" className="mt-6 flex items-start gap-3 p-4 bg-brand-green-50 border border-brand-green/10 rounded-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-brand-ink">Thank you. We've received your message and will respond shortly.</div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-brand-green-50/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Find us</div>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight">Visakhapatnam · Andhra Pradesh</h2>
          <div className="mt-10 relative aspect-[16/7] rounded-sm overflow-hidden shadow-glass border border-black/5">
            <iframe data-testid="contact-map" title="Worldly Solutions HQ" src={COMPANY.mapEmbed} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
